import { useState } from 'react'
import { useComplianceDocs, DOC_TYPES, URGENCY_STYLES } from '../hooks/useComplianceDocs.js'
import ComplianceDocEditModal from './ComplianceDocEditModal.jsx'

// Compliance documents section — lives inside SettingsScreen.
//
// Top section: a compact summary of the urgency picture (X expired,
// Y expiring within 30 days, etc.) so the manager doesn't have to
// scan the full list to know if anything's on fire.
//
// Below: a flat list of all docs sorted by expiration ascending, with
// urgency colour bands. Tap a doc to edit. "Add document" button at
// the top opens the edit modal in create mode.
export default function ComplianceDocsSection() {
  const { docs, counts, loading, error, addDocument, updateDocument, archiveDocument } = useComplianceDocs()

  const [editingDoc, setEditingDoc] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)

  const docTypeLabels = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t.label]))

  return (
    <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <header className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-black/55">
            Compliance documents
          </div>
          <p className="mt-1 text-xs text-black/60 leading-snug">
            Liquor license, food handler certs, insurance — everything that quietly
            becomes a $5-50k problem when it lapses.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          + Add
        </button>
      </header>

      {loading && (
        <div className="mt-3 text-sm text-black/55">Loading…</div>
      )}

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Headline urgency strip — only shown when there's anything to flag. */}
      {!loading && (counts.expired || counts.expires_in_7d || counts.expires_in_30d) ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {counts.expired ? (
            <UrgencyPill count={counts.expired} label="Expired" tone="red" />
          ) : null}
          {counts.expires_in_7d ? (
            <UrgencyPill count={counts.expires_in_7d} label="≤7 days" tone="red" />
          ) : null}
          {counts.expires_in_30d ? (
            <UrgencyPill count={counts.expires_in_30d} label="≤30 days" tone="amber" />
          ) : null}
        </div>
      ) : null}

      {/* Empty state */}
      {!loading && docs.length === 0 && !error && (
        <div className="mt-3 rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/65">
          No compliance documents tracked yet. Tap{' '}
          <span className="font-medium text-blue-600">+ Add</span>{' '}
          to log your first one.
        </div>
      )}

      {/* Document list */}
      {docs.length > 0 && (
        <ul className="mt-3 divide-y divide-black/10 rounded-xl border border-black/10 bg-white">
          {docs.map((doc) => {
            const style = URGENCY_STYLES[doc.urgency] || URGENCY_STYLES.active
            return (
              <li key={doc.id}>
                <button
                  type="button"
                  onClick={() => setEditingDoc(doc)}
                  className={`flex w-full items-start gap-3 px-3 py-3 text-left transition hover:bg-black/[0.02] ${style.bg}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-sm font-medium ${style.text}`}>
                      {doc.name}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-black/55">
                      {docTypeLabels[doc.doc_type] || doc.doc_type}
                      {doc.holder_name ? ` · ${doc.holder_name}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${style.pill}`}>
                      {style.label}
                    </div>
                    <div className="mt-1 text-xs tabular-nums text-black/65">
                      {doc.expiration_date
                        ? formatDate(doc.expiration_date)
                        : '—'}
                      {doc.days_until_expiration != null && (
                        <span className="ml-1 text-black/45">
                          {doc.days_until_expiration < 0
                            ? `(${Math.abs(doc.days_until_expiration)}d ago)`
                            : `(in ${doc.days_until_expiration}d)`}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <ComplianceDocEditModal
        open={createOpen}
        doc={null}
        onClose={() => setCreateOpen(false)}
        onSave={addDocument}
      />
      <ComplianceDocEditModal
        open={!!editingDoc}
        doc={editingDoc}
        onClose={() => setEditingDoc(null)}
        onSave={(payload) => updateDocument(editingDoc.id, payload)}
        onArchive={archiveDocument}
      />
    </section>
  )
}

function UrgencyPill({ count, label, tone }) {
  const styles = {
    red:   'bg-red-100 text-red-900 border-red-200',
    amber: 'bg-amber-100 text-amber-900 border-amber-200',
  }[tone] || 'bg-black/5 text-black/70'
  return (
    <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${styles}`}>
      {count} {label}
    </span>
  )
}

function formatDate(isoDate) {
  if (!isoDate) return ''
  // isoDate is YYYY-MM-DD; format to a friendly local-ish string
  const [y, m, d] = isoDate.split('-')
  return `${m}/${d}/${y}`
}
